import React, { useMemo, useState } from 'react';
import { ArrowLeft, Download, Search, Filter, ChevronDown, ChevronRight, Shield, AlertTriangle, Cloud, Server, Database, Users, ShieldCheck, Sparkles, ExternalLink } from 'lucide-react';
import type { Environment } from '../components/RecentScans';
interface RemediationFinding {
  id: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  resourceType: string;
  resourceName: string;
  affected: number;
}
interface VulnerabilityAnalysisProps {
  onViewClientVulns: (env: Environment) => void;
  onBack: () => void;
}
const allEnvironments: Environment[] = [{
  id: 'ENV-001',
  clientName: 'Acme Corp',
  type: 'Hybrid',
  status: 'healthy',
  identities: 12450,
  complianceScore: 94,
  overPrivileged: 12,
  lastSync: '2m ago'
}, {
  id: 'ENV-002',
  clientName: 'Globex Inc',
  type: 'AWS',
  status: 'warning',
  identities: 3840,
  complianceScore: 78,
  overPrivileged: 45,
  lastSync: '15m ago'
}, {
  id: 'ENV-003',
  clientName: 'Soylent Corp',
  type: 'On-Prem',
  status: 'critical',
  identities: 850,
  complianceScore: 62,
  overPrivileged: 128,
  lastSync: '1h ago'
}, {
  id: 'ENV-004',
  clientName: 'Umbrella Corp',
  type: 'Azure',
  status: 'healthy',
  identities: 15200,
  complianceScore: 98,
  overPrivileged: 3,
  lastSync: '5m ago',
  govCloud: true,
  govCloudRegion: 'USGov Virginia',
  cmmcLevel: 3,
  complianceFrameworks: ['NIST 800-171', 'CMMC', 'FedRAMP High', 'ITAR']
}, {
  id: 'ENV-005',
  clientName: 'Stark Ind',
  type: 'GCP',
  status: 'warning',
  identities: 5600,
  complianceScore: 85,
  overPrivileged: 22,
  lastSync: '30m ago',
  govCloud: true,
  govCloudRegion: 'us-gov-west1',
  cmmcLevel: 2,
  complianceFrameworks: ['NIST 800-171', 'CMMC', 'FedRAMP Moderate']
}];
function getFindingsForEnv(env: Environment): RemediationFinding[] {
  switch (env.clientName) {
    case 'Acme Corp':
      return [{
        id: 'REM-A01',
        severity: 'LOW',
        title: 'Unused service accounts older than 90 days',
        resourceType: 'Service Account',
        resourceName: 'svc-acme-legacy',
        affected: 4
      }, {
        id: 'REM-A02',
        severity: 'LOW',
        title: 'Password policy below recommended strength',
        resourceType: 'IAM Policy',
        resourceName: 'default-password-policy',
        affected: 2
      }];
    case 'Globex Inc':
      return [{
        id: 'REM-G01',
        severity: 'MEDIUM',
        title: 'Overly Broad S3 Bucket Policy',
        resourceType: 'S3 Bucket Policy',
        resourceName: 'globex-data-lake',
        affected: 5
      }, {
        id: 'REM-G02',
        severity: 'MEDIUM',
        title: 'Cross-Account Role Trust Too Permissive',
        resourceType: 'IAM Role',
        resourceName: 'role-globex-cross-acct',
        affected: 3
      }, {
        id: 'REM-G03',
        severity: 'LOW',
        title: 'CloudWatch Log Group Retention Not Set',
        resourceType: 'CloudWatch',
        resourceName: '/aws/lambda/globex-api',
        affected: 8
      }, {
        id: 'REM-G04',
        severity: 'HIGH',
        title: 'Shadow AI Copilot with Tenant-Wide OAuth Consent',
        resourceType: 'OAuth App Registration',
        resourceName: 'app-globex-ai-assistant-unregistered',
        affected: 6
      }];
    case 'Soylent Corp':
      return [{
        id: 'REM-S01',
        severity: 'HIGH',
        title: 'Service Account with Owner Role',
        resourceType: 'Service Principal',
        resourceName: 'sp-soylent-corp-deploy',
        affected: 3
      }, {
        id: 'REM-S02',
        severity: 'HIGH',
        title: 'Stale Admin Account — No MFA',
        resourceType: 'User Account',
        resourceName: 'admin-legacy@soylent.com',
        affected: 1
      }, {
        id: 'REM-S03',
        severity: 'MEDIUM',
        title: 'Wildcard Permissions on IAM Policy',
        resourceType: 'IAM Policy',
        resourceName: 'policy-soylent-legacy',
        affected: 12
      }, {
        id: 'REM-S04',
        severity: 'MEDIUM',
        title: 'Unused API Keys — Active 180+ Days',
        resourceType: 'Access Key',
        resourceName: 'AKIA...X7Q9',
        affected: 2
      }, {
        id: 'REM-S05',
        severity: 'HIGH',
        title: 'Unregistered AI Agent with Admin API Access',
        resourceType: 'Service Account (Shadow AI)',
        resourceName: 'svc-soylent-llm-agent-prod',
        affected: 8
      }];
    case 'Umbrella Corp':
      return [{
        id: 'REM-U01',
        severity: 'LOW',
        title: 'Conditional Access Policy Gap for Guest Users',
        resourceType: 'Conditional Access',
        resourceName: 'CA-Policy-Guests',
        affected: 3
      }, {
        id: 'REM-U02',
        severity: 'LOW',
        title: 'Service Principal Certificate Expiring in 30 Days',
        resourceType: 'Service Principal',
        resourceName: 'sp-umbrella-reporting',
        affected: 1
      }];
    case 'Stark Ind':
      return [{
        id: 'REM-ST01',
        severity: 'MEDIUM',
        title: 'Overly Permissive IAM Binding on Project',
        resourceType: 'IAM Binding',
        resourceName: 'project-stark-weapons-dev',
        affected: 6
      }, {
        id: 'REM-ST02',
        severity: 'MEDIUM',
        title: 'Service Account Key Not Rotated in 180 Days',
        resourceType: 'Service Account Key',
        resourceName: 'sa-jarvis-backend',
        affected: 4
      }, {
        id: 'REM-ST03',
        severity: 'LOW',
        title: 'Audit Log Sink Missing for Critical Projects',
        resourceType: 'Cloud Logging',
        resourceName: 'sink-stark-audit',
        affected: 2
      }, {
        id: 'REM-ST04',
        severity: 'MEDIUM',
        title: 'Agentic AI Workflow with Autonomous Resource Provisioning',
        resourceType: 'Service Account (Shadow AI)',
        resourceName: 'sa-jarvis-agent-orchestrator',
        affected: 14
      }];
    default:
      return [];
  }
}
export function VulnerabilityAnalysis({
  onViewClientVulns,
  onBack
}: VulnerabilityAnalysisProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');
  const [typeFilter, setTypeFilter] = useState<'All' | 'Cloud' | 'Hybrid' | 'On-Prem'>('All');
  const [sortBy, setSortBy] = useState<'severity' | 'findings' | 'compliance'>('severity');
  const envWithFindings = useMemo(() => {
    return allEnvironments.map(env => {
      const findings = getFindingsForEnv(env);
      return {
        env,
        findings,
        highCount: findings.filter(f => f.severity === 'HIGH').length,
        mediumCount: findings.filter(f => f.severity === 'MEDIUM').length,
        lowCount: findings.filter(f => f.severity === 'LOW').length,
        totalAffected: findings.reduce((sum, f) => sum + f.affected, 0)
      };
    });
  }, []);
  const totals = useMemo(() => {
    return envWithFindings.reduce((acc, e) => ({
      findings: acc.findings + e.findings.length,
      high: acc.high + e.highCount,
      medium: acc.medium + e.mediumCount,
      low: acc.low + e.lowCount,
      affected: acc.affected + e.totalAffected,
      identities: acc.identities + e.env.identities
    }), {
      findings: 0,
      high: 0,
      medium: 0,
      low: 0,
      affected: 0,
      identities: 0
    });
  }, [envWithFindings]);
  const filtered = useMemo(() => {
    let result = envWithFindings;
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => e.env.clientName.toLowerCase().includes(q) || e.env.type.toLowerCase().includes(q) || e.findings.some(f => f.title.toLowerCase().includes(q)));
    }
    // Severity filter — only show envs that have findings of that severity
    if (severityFilter !== 'ALL') {
      result = result.filter(e => e.findings.some(f => f.severity === severityFilter));
    }
    // Type filter
    if (typeFilter !== 'All') {
      result = result.filter(e => {
        if (typeFilter === 'Cloud') return ['AWS', 'Azure', 'GCP'].includes(e.env.type);
        if (typeFilter === 'Hybrid') return e.env.type === 'Hybrid';
        if (typeFilter === 'On-Prem') return e.env.type === 'On-Prem';
        return true;
      });
    }
    // Sort
    result = [...result].sort((a, b) => {
      if (sortBy === 'severity') {
        if (a.highCount !== b.highCount) return b.highCount - a.highCount;
        if (a.mediumCount !== b.mediumCount) return b.mediumCount - a.mediumCount;
        return b.findings.length - a.findings.length;
      }
      if (sortBy === 'findings') return b.findings.length - a.findings.length;
      if (sortBy === 'compliance') return a.env.complianceScore - b.env.complianceScore;
      return 0;
    });
    return result;
  }, [envWithFindings, searchQuery, severityFilter, typeFilter, sortBy]);
  const getStatusColor = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-astro-green';
      case 'warning':
        return 'bg-yellow-400';
      case 'critical':
        return 'bg-astro-danger';
      default:
        return 'bg-astro-text-secondary';
    }
  };
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={14} data-id="element-2527" />;
      case 'On-Prem':
        return <Server size={14} data-id="element-2528" />;
      case 'Hybrid':
        return <Database size={14} data-id="element-2529" />;
      default:
        return <Server size={14} data-id="element-2530" />;
    }
  };
  const getSeverityStyle = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'bg-astro-danger/10 text-astro-danger border-astro-danger/20';
      case 'MEDIUM':
        return 'bg-white/5 text-astro-text-secondary border-white/10';
      case 'LOW':
        return 'bg-[#1f2937] text-gray-400 border-gray-700';
      default:
        return 'bg-white/5 text-astro-text-secondary border-white/10';
    }
  };
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-2531">
      {/* Header */}
      <div className="mb-8" data-id="element-2532">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-astro-text-secondary hover:text-white transition-colors mb-4" data-id="element-2533">
          <ArrowLeft size={16} data-id="element-2534" />
          Back to Dashboard
        </button>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-2535">
          <div data-id="element-2536">
            <h1 className="text-3xl font-bold text-white mb-2" data-id="element-2537">
              Vulnerability <span className="text-astro-green" data-id="element-2538">Overview</span>
            </h1>
            <p className="text-astro-text-secondary" data-id="element-2539">
              Aggregated security findings across all {allEnvironments.length}{' '}
              managed environments
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2540">
            <button className="flex items-center gap-2 px-4 py-2 border border-astro-border text-astro-text-secondary rounded text-sm font-medium hover:text-white hover:border-white/30 transition-colors" data-id="element-2541">
              <Download size={16} data-id="element-2542" />
              Export All
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-id="element-2543">
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2544">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2545">
            <Shield size={10} data-id="element-2546" /> Total Findings
          </div>
          <div className="text-2xl font-mono font-bold text-white" data-id="element-2547">
            {totals.findings}
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2548">
            across {allEnvironments.length} environments
          </div>
        </div>
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2549">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2550">
            <AlertTriangle size={10} className="text-astro-danger" data-id="element-2551" /> High
            Severity
          </div>
          <div className="text-2xl font-mono font-bold text-astro-danger" data-id="element-2552">
            {totals.high}
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2553">
            require immediate action
          </div>
        </div>
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2554">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1" data-id="element-2555">
            Medium Severity
          </div>
          <div className="text-2xl font-mono font-bold text-white" data-id="element-2556">
            {totals.medium}
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2557">
            should be addressed
          </div>
        </div>
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2558">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1" data-id="element-2559">
            Low Severity
          </div>
          <div className="text-2xl font-mono font-bold text-astro-text-secondary" data-id="element-2560">
            {totals.low}
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2561">
            informational
          </div>
        </div>
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2562">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2563">
            <Users size={10} data-id="element-2564" /> Affected Resources
          </div>
          <div className="text-2xl font-mono font-bold text-white" data-id="element-2565">
            {totals.affected}
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2566">
            identities & resources
          </div>
        </div>
        <div className="bg-astro-card border border-astro-border rounded-lg p-4" data-id="element-2567">
          <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2568">
            <ShieldCheck size={10} data-id="element-2569" /> Avg Compliance
          </div>
          <div className={`text-2xl font-mono font-bold ${Math.round(allEnvironments.reduce((s, e) => s + e.complianceScore, 0) / allEnvironments.length) > 80 ? 'text-astro-green' : 'text-astro-danger'}`} data-id="element-2570">
            {Math.round(allEnvironments.reduce((s, e) => s + e.complianceScore, 0) / allEnvironments.length)}
            %
          </div>
          <div className="text-[10px] text-astro-text-secondary" data-id="element-2571">
            across all clients
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6 items-end lg:items-center" data-id="element-2572">
        <div className="flex-1 w-full" data-id="element-2573">
          <div className="relative" data-id="element-2574">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" size={16} data-id="element-2575" />

            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search clients or findings..." className="w-full bg-astro-card border border-astro-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/40 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-2576" />
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-2577">
          <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider mr-1 flex items-center gap-1" data-id="element-2578">
            <Filter size={10} data-id="element-2579" /> Severity
          </span>
          {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(sev => <button key={sev} onClick={() => setSeverityFilter(sev)} className={`px-2.5 py-1.5 text-[11px] font-bold rounded border transition-colors ${severityFilter === sev ? sev === 'HIGH' ? 'bg-astro-danger/15 border-astro-danger/30 text-astro-danger' : sev === 'ALL' ? 'bg-astro-green/15 border-astro-green/30 text-astro-green' : sev === 'MEDIUM' ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 border-white/10 text-astro-text-secondary' : 'bg-transparent border-astro-border text-astro-text-secondary/50 hover:text-astro-text-secondary hover:border-white/10'}`} data-id="element-2580">
              {sev === 'ALL' ? 'All' : sev}
            </button>)}
        </div>

        <div className="flex items-center gap-2" data-id="element-2581">
          {(['All', 'Cloud', 'Hybrid', 'On-Prem'] as const).map(t => <button key={t} onClick={() => setTypeFilter(t)} className={`px-2.5 py-1.5 text-[11px] font-bold rounded border transition-colors ${typeFilter === t ? 'bg-astro-green/15 border-astro-green/30 text-astro-green' : 'bg-transparent border-astro-border text-astro-text-secondary/50 hover:text-astro-text-secondary hover:border-white/10'}`} data-id="element-2582">
              {t}
            </button>)}
        </div>

        <div className="relative" data-id="element-2583">
          <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="bg-astro-card border border-astro-border rounded-lg pl-3 pr-8 py-2.5 text-xs text-white appearance-none focus:outline-none focus:border-astro-green/50 cursor-pointer" data-id="element-2584">
            <option value="severity" data-id="element-2585">Sort: Severity</option>
            <option value="findings" data-id="element-2586">Sort: Most Findings</option>
            <option value="compliance" data-id="element-2587">Sort: Lowest Compliance</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-astro-text-secondary pointer-events-none" size={14} data-id="element-2588" />
        </div>
      </div>

      {/* Environment Cards */}
      <div className="space-y-4" data-id="element-2589">
        {filtered.map(({
        env,
        findings,
        highCount,
        mediumCount,
        lowCount,
        totalAffected
      }) => <div key={env.id} className="bg-astro-card border border-astro-border rounded-lg overflow-hidden transition-all" data-id="element-2590">
              {/* Environment Header Row */}
              <div className="p-5" data-id="element-2591">
                <div className="flex flex-col lg:flex-row gap-5" data-id="element-2592">
                  {/* Left: Identity */}
                  <div className="flex-1 min-w-0" data-id="element-2593">
                    <div className="flex items-center gap-2.5 mb-2 flex-wrap" data-id="element-2594">
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${getStatusColor(env.status)}`} data-id="element-2595"></div>
                      <h3 className="text-lg font-bold text-white" data-id="element-2596">
                        {env.clientName}
                      </h3>
                      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-white/5 text-astro-text-secondary border-white/10" data-id="element-2597">
                        {getTypeIcon(env.type)}
                        {env.type}
                      </span>
                      {env.govCloud && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-blue-500/10 text-blue-400 border-blue-500/25" data-id="element-2598">
                          <Shield size={10} data-id="element-2599" />
                          GOV
                        </span>}
                      {env.status !== 'healthy' && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${env.status === 'critical' ? 'bg-astro-danger/10 text-astro-danger border-astro-danger/20' : 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20'}`} data-id="element-2600">
                          {env.status}
                        </span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-astro-text-secondary font-mono" data-id="element-2601">
                      <span data-id="element-2602">{env.id}</span>
                      <span className="text-astro-border" data-id="element-2603">·</span>
                      <span data-id="element-2604">{env.identities.toLocaleString()} identities</span>
                      <span className="text-astro-border" data-id="element-2605">·</span>
                      <span data-id="element-2606">Synced {env.lastSync}</span>
                    </div>
                  </div>

                  {/* Middle: Severity Breakdown */}
                  <div className="flex items-center gap-3 shrink-0" data-id="element-2607">
                    {highCount > 0 && <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-astro-danger/10 border border-astro-danger/20" data-id="element-2608">
                        <span className="w-2 h-2 rounded-full bg-astro-danger" data-id="element-2609"></span>
                        <span className="text-sm font-mono font-bold text-astro-danger" data-id="element-2610">
                          {highCount}
                        </span>
                        <span className="text-[10px] text-astro-danger/70" data-id="element-2611">
                          HIGH
                        </span>
                      </div>}
                    {mediumCount > 0 && <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10" data-id="element-2612">
                        <span className="w-2 h-2 rounded-full bg-astro-text-secondary" data-id="element-2613"></span>
                        <span className="text-sm font-mono font-bold text-white" data-id="element-2614">
                          {mediumCount}
                        </span>
                        <span className="text-[10px] text-astro-text-secondary" data-id="element-2615">
                          MED
                        </span>
                      </div>}
                    {lowCount > 0 && <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]" data-id="element-2616">
                        <span className="w-2 h-2 rounded-full bg-gray-500" data-id="element-2617"></span>
                        <span className="text-sm font-mono font-bold text-astro-text-secondary" data-id="element-2618">
                          {lowCount}
                        </span>
                        <span className="text-[10px] text-astro-text-secondary/60" data-id="element-2619">
                          LOW
                        </span>
                      </div>}
                    {findings.length === 0 && <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-astro-green/5 border border-astro-green/15" data-id="element-2620">
                        <ShieldCheck size={14} className="text-astro-green" data-id="element-2621" />
                        <span className="text-xs font-bold text-astro-green" data-id="element-2622">
                          Clean
                        </span>
                      </div>}
                  </div>

                  {/* Right: Compliance + Action */}
                  <div className="flex items-center gap-4 shrink-0" data-id="element-2623">
                    <div className="text-right" data-id="element-2624">
                      <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-0.5" data-id="element-2625">
                        Compliance
                      </div>
                      <span className={`text-xl font-mono font-bold ${env.complianceScore > 85 ? 'text-astro-green' : env.complianceScore > 70 ? 'text-white' : 'text-astro-danger'}`} data-id="element-2626">
                        {env.complianceScore}%
                      </span>
                    </div>
                    <button onClick={() => onViewClientVulns(env)} className="flex items-center gap-2 px-4 py-2.5 bg-astro-bg border border-astro-border rounded-lg text-sm font-medium text-astro-text-secondary hover:text-white hover:border-astro-green/50 hover:bg-astro-green/5 transition-all whitespace-nowrap" data-id="element-2627">
                      <Sparkles size={14} data-id="element-2628" />
                      Remediate
                      <ChevronRight size={14} data-id="element-2629" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Findings Preview */}
              {findings.length > 0 && <div className="border-t border-astro-border bg-[#0a0e14]/50" data-id="element-2630">
                  <div className="px-5 py-3 space-y-0" data-id="element-2631">
                    {findings.slice(0, 3).map((finding, i) => <div key={finding.id} className="flex items-center gap-3 py-2 border-b border-astro-border/50 last:border-b-0" data-id="element-2632">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border shrink-0 ${getSeverityStyle(finding.severity)}`} data-id="element-2633">
                          {finding.severity}
                        </span>
                        <span className="text-xs text-astro-text-secondary truncate flex-1" data-id="element-2634">
                          {finding.title}
                        </span>
                        <span className="text-[10px] text-astro-text-secondary/50 shrink-0 font-mono" data-id="element-2635">
                          {finding.affected} affected
                        </span>
                      </div>)}
                    {findings.length > 3 && <button onClick={() => onViewClientVulns(env)} className="flex items-center gap-1 py-2 text-[11px] font-medium text-astro-green hover:text-astro-green-dark transition-colors" data-id="element-2636">
                        +{findings.length - 3} more findings
                        <ExternalLink size={10} data-id="element-2637" />
                      </button>}
                  </div>
                </div>}
            </div>)}

        {filtered.length === 0 && <div className="py-16 text-center bg-astro-card border border-astro-border rounded-lg" data-id="element-2638">
            <Shield size={32} className="text-astro-text-secondary/30 mx-auto mb-3" data-id="element-2639" />

            <p className="text-sm text-astro-text-secondary mb-1" data-id="element-2640">
              No environments match your filters.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSeverityFilter('ALL');
          setTypeFilter('All');
        }} className="text-xs text-astro-green hover:text-astro-green-dark transition-colors" data-id="element-2641">
              Clear all filters
            </button>
          </div>}
      </div>
    </div>;
}